import * as angular from 'angular';
import * as moment from 'moment-business-days';
import { parseDomain } from 'helpers/parseDomain';

import {
  Input,
  Inject,
  OnInit,
  Component,
} from 'ng-metadata/core';

import { CartService } from 'services/cart.service';
import { ProductService } from 'services/product.service';
import { EcommerceService } from 'services/ecommerce.service';

import passportProcessingHtml from './passport-processing.html';
import './passport-processing.scss';

/**
 * Passport processing form component class.
 */
@Component({
  selector: 'gwPassportProcessing',
  template: passportProcessingHtml,
})
export class PassportProcessingComponent implements OnInit {
  public GW = (<any>window).GW;
  public fbq = (<any>window).fbq;
  public laroute = (<any>window).laroute;

  public processingForm;
  public cart = this.cartService.getCart();
  public product = this.GW.product;
  public passportProducts = this.GW.passportProducts;
  public processingOptions = this.productService
    .mapPassportOptions(this.GW.processingOptions);
  public departureDate

  public model = {
    addons: [],
    product: null,
    processing: null,
    traveler: {
      firstName: null,
      lastName: null,
      dob: null,
      destination: null,
      departure: null,
    },
  };

  public state = {
    ready: false,
    parsed: null,
    options: null,
    product: null,
    dateOfBirth: null,
    destination: null,
    departureDate: null,
    submitting: false,
    addonOptions: null,
    renewalConfirm: false,
    renewalConfirmed: false,
    destinationQuery: null,
    dateOfBirthMin: '1868-01-01',
    countries: this.GW.countries,
    today: moment().format('YYYY-MM-DD'),
    dobChild: moment().subtract(16, 'years').format('YYYY-MM-DD'),
    minimumTravel: moment().businessAdd(1).add(1, 'day').format('YYYY-MM-DD'),
    outboundOptions: this.productService
      .mapOutboundOptions(this.GW.outboundOptions),
  };

  @Input() optionSlug: string;
  @Input() productSlug: string;

  constructor(
    @Inject('$filter') private $filter: ng.IFilterService,
    @Inject('$rootScope') private $rootScope: ng.IRootScopeService,
    @Inject(CartService) private cartService: CartService,
    @Inject(ProductService) private productService: ProductService,
    @Inject(EcommerceService) private ecommerceService: EcommerceService,
  ) {}

  /**
   * Component init
   */
  ngOnInit() {
    const parsed = parseDomain(window.location.href);
    const rev = parsed.domain !== 'passportrenewal';

    this.state.options =
      this.$filter('orderBy')(this.processingOptions.options, 'processingFee', rev);

    this.state.addonOptions = this.processingOptions.addons;

    // Define Passport Card note manually until admin support is added
    const cardAddon = this.state.addonOptions
      .find(addon => addon.label === 'Passport Card');

    if (cardAddon) {
      cardAddon.description = `An easy to carry wallet sized card that
        is a useful form of secondary ID.`;
      cardAddon.note = `A Passport Card is a wallet sized passport, similar to a state driver’s
        license. It is an easy to carry ID for international travel by land or water to Canada,
        Mexico, the Caribbean and Bermuda.`;
    }

    // Define Passport Card note manually until admin support is added
    const insuranceAddon = this.state.addonOptions
      .find(addon => addon.label === 'Passport Insurance');

    if (insuranceAddon) {
      insuranceAddon.description = `In the event your passport is lost, stolen or damaged
                                    govWorks will expedite a replacement at no cost.`;
      insuranceAddon.note = `Passport Insurance is a great way to protect yourself if your
        passport goes missing and you need to get a replacement in a hurry. The insurance
        covers the expediting cost for the duration of your passport’s validity.`;
    }

    // Select product from URL if it's defined
    if (this.productSlug) {
      const product = this.passportProducts.find(product => product.subtype === this.productSlug);
      this.model.product = product ? product.uuid : null;
    }

    // Select option from URL if it's defined
    // if (this.optionSlug) {
    //   const preselected = this.state.options.find((opt) => {
    //     return this.optionSlug === this.$filter('slugify')(opt.label);
    //   });

    //   if (preselected) {
    //     this.model.processing = preselected.uuid;
    //   }
    // }
    // else {
    //   // Select recommended expediting by default
    //   const recommended = this.state.options.find((opt) => {
    //     return opt.recommended === true;
    //   });

    //   if (recommended) {
    //     this.model.processing = recommended.uuid;
    //   }
    // }

    // If the cart is clean, setup the summary from selected options
    this.setupCart();

    // Send ecommerce checkout step
    this.ecommerceService.checkout(1, this.cart);

    // Send ecommerce product impressions
    this.ecommerceService
      .productImpressions(this.passportProducts.map((product) => {
        return {
          id: product.subtype,
          name: `US ${product.label}`,
          brand: 'govWorks',
          category: product.type,
        };
      }));
  }

  setupCart() {
    // If an item has been added to cart, set options from it
    if (this.cart.order.length) {
      const orderItem = this.cart.order[0];

      // Set product
      const product = this.passportProducts
        .find(product => product.uuid === orderItem.productId);
      this.model.product = product.uuid;

      // Set application from traveler info
      const traveler = this.cart.travelers[orderItem.travelerId];

      if (traveler) {
        this.model.traveler.firstName = traveler.firstName;
        this.model.traveler.lastName = traveler.lastName;
        this.state.dateOfBirth = moment(traveler.dob, ['MM/DD/YYYY', moment.ISO_8601]).format('MM/DD/YYYY');
        this.updateTravelerDob();
      }

      // Set optional travel itinerary information
      const itinerary = this.cart.itinerary.destinations[orderItem.itineraryId];

      if (itinerary) {
        this.state.destinationQuery = itinerary.country;
        this.model.traveler.destination = itinerary.country;

        if (itinerary.dates.startDate) {
          this.state.departureDate = moment(itinerary.dates.startDate).format('MM/DD/YYYY')
          this.model.traveler.departure = moment(itinerary.dates.startDate).format('MM/DD/YYYY');
        }
      }

      // Set options
      this.model.processing = orderItem.optionsId;

      // Set addons
      this.model.addons = orderItem.addons;

      return;
    }

    if (this.model.addons.length == 0) {
      const addon = this.state.addonOptions.find(addon => addon.label === 'Passport Insurance')
      this.model.addons.push(addon)
    }

    this.updateProduct();
    this.updateCartOptions();
    this.updateOrderItem();
  }

  selectProduct($event) {
    const productId = $event ? $event.productId : null;

    if (!productId) {
      return;
    }

    this.model.product = productId;
    this.updateProduct(true);
  }

  async updateProduct(resetMailaway = false) {
    if (this.determineRenewalWizard()) {
      return;
    }

    // Add product to cart
    // @TODO add support for multiple products
    this.cart.products = {};

    if (this.model.product) {
      this.cartService
        .addProduct(this.passportProducts.find(product => product.uuid === this.model.product));

      // Update date of birth minimum
      this.determineDateOfBirthMin();

      if (resetMailaway) {
        this.model.processing = null; // reset expediting option as well
        this.state.renewalConfirm = false;
        this.state.renewalConfirmed = false;
      }

      this.updateOrderItem();
    }
  }

  updateCartOptions() {
    if (this.determineRenewalWizard()) {
      return;
    }

    const option = angular.copy(this.state.options
      .find(opt => opt.uuid === this.model.processing));

    if (option) {
      // @todo Add multi traveler support
      this.cart.options = {};

      // Set fixed inbound shipping option for mailaways
      const product = this.passportProducts
        .find(product => product.uuid === this.model.product);

      if (this.cartService.determineMailaway(product, option)) {
        // Manually change inbound shipping option for mailaways
        const priority = this.state.outboundOptions.find(opt => opt.value === 'priority_overnight');

        if (priority) {
          option.inboundShipping = priority.price;
          option.inboundShippingLabel = priority.label;
        }
      }

      this.cartService.addOptions(option);

      this.updateProduct();
      this.updateOrderItem();

      // Broadcast event to start checkout timer on a separate component
      this.$rootScope.$broadcast('display-timer');
    }
  }

  addTraveler() {
    // @todo Update for multi traveler support
    const traveler = angular.copy(this.cartService.newTraveler);
    traveler.firstName = this.model.traveler.firstName;
    traveler.lastName = this.model.traveler.lastName;

    if (this.model.traveler.dob) {
      traveler.dob = this.model.traveler.dob;
    }

    this.cart.travelers = {
      t1: traveler,
    };
  }

  addItinerary() {
    const itinerary = {
      country: this.model.traveler.destination,
      dates: {
        startDate: null,
        endDate: null,
      },
    };

    if (this.model.traveler.departure) {
      const date = this.model.traveler.departure;
      itinerary.dates.startDate = date;
    }

    this.cart.itinerary.destinations = {
      t1: itinerary,
    };
  }

  async updateOrderItem() {
    // Add order item to relate these data points, no traveler
    const orderItem = {
      type: 'passport',
      outbound: null,
      travelerId: 't1',
      itineraryId: 't1',
      addons: this.model.addons,
      productId: this.model.product,
      optionsId: this.model.processing,
      requireShipping: true
    };

    // Preselect outbound option if mailaway
    const product = this.cart.products[this.model.product];
    const option = this.cart.options[this.model.processing];

    orderItem.requireShipping = product.require_shipping

    if (this.cartService.determineMailaway(product, option)) {
      let optionId = 'usps_priority_3_days';

      orderItem.outbound = this.state.outboundOptions
        .find(opt => opt.value === optionId);
    } else if (this.model.processing) {
      // Pre-select cheapest option
      orderItem.outbound = this.state.outboundOptions
        .find(opt => opt.value === 'standard_overnight');
    }

    // Add item to order
    // @todo Temporary until multiple traveler support is added
    this.cart.order = [orderItem];
  }

  updateTravelerDob() {
    if (this.processingForm) {
      this.checkDate('dob', this.state.dateOfBirthMin, this.state.today, this.state.dateOfBirth)
    }
    
    this.model.traveler.dob =
      moment(this.state.dateOfBirth, ['MM/DD/YYYY', moment.ISO_8601]).format('MM/DD/YYYY')
  }

  updateTravelerDeparture(newDate) {
    if (newDate || this.state.departureDate) {
      const date = newDate || moment(this.state.departureDate)

      if (newDate) {
        this.state.departureDate = date.format('MM/DD/YYYY')
      }
  
      this.checkDate('departure', this.state.minimumTravel, '2038-12-31', this.state.departureDate)

      if (Object.keys(this.processingForm.departure.$error).length == 0 && date.isValid()) {
        this.departureDate = date.format('MM/DD/YYYY')
      }

      this.model.traveler.departure = date.format('MM/DD/YYYY')
    } else {
      this.model.traveler.departure = null
      this.processingForm.departure.$setValidity('date', true)
      this.processingForm.departure.$setValidity('min', true)
      this.processingForm.departure.$setValidity('max', true)
    }
  }

  // Fixes Departure Date value when user removes date or types a wrong one.
  checkDate(name: string, min, max, newValue) {
    let value = newValue ? newValue : this.processingForm[name].$viewValue

    if (!moment(value, 'MM/DD/YYYY', true).isValid()) {
      this.processingForm[name].$setValidity('date', false)
      this.model.traveler[name] = null;
    } else {
      this.processingForm[name].$setValidity('date', true)

      if (moment(value, 'MM/DD/YYYY', true).isBefore(min)) {
        this.processingForm[name].$setValidity('min', false)
      } else {
        this.processingForm[name].$setValidity('min', true)
      }

      if (moment(value, 'MM/DD/YYYY', true).isAfter(max)) {
        this.processingForm[name].$setValidity('max', false)
      } else {
        this.processingForm[name].$setValidity('max', true)
      }
    }
  }

  toggleAddon(chosenAddon) {
    const i = this.model.addons.findIndex(addon => addon.uuid === chosenAddon.uuid);

    if (i > -1) {
      this.model.addons.splice(i, 1);
    } else {
      this.model.addons.push(chosenAddon);
    }

    this.updateOrderItem();
  }

  addonExists(addonId) {
    return this.model.addons.findIndex(addon => addon.uuid === addonId) > -1;
  }

  showOption(opt: any) {
    if (!this.model.product) {
      return true;
    }

    const product = this.passportProducts
      .find(product => product.uuid === this.model.product);

    if (product && product.subtype !== 'passport-renewal' && opt.renewal) {
      return false;
    }

    return true;
  }

  showInboundShipping() {
    const opt = this.state.options.find((option) => {
      return option.uuid === this.model.processing;
    });

    if (!opt) {
      return;
    }

    return this.$filter('currency')(opt.inboundShipping);
  }

  showInboundShippingLabel() {
    const opt = this.state.options.find((option) => {
      return option.uuid === this.model.processing;
    });

    if (!opt) {
      return;
    }

    return opt.inboundShippingLabel;
  }

  determineGovFee() {
    const product = this.passportProducts
      .find(product => product.uuid === this.model.product);
    const option = this.processingOptions.options
      .find(option => option.uuid === this.model.processing);

    const fee = this.cartService
      .determineGovFee(product, this.model.addons, option);

    return this.$filter('currency')(fee);
  }

  determineApplicationAge() {
    const diff = moment().diff(moment(this.model.traveler.dob, 'MM/DD/YYYY'), 'years');

    if (diff) {
      return Math.abs(diff);
    }

    return;
  }

  determineRenewalWizard() {
    if (this.state.renewalConfirmed) {
      return;
    }

    // Display renewal confirm wizard if necessary
    const option = this.state.options
      .find(opt => opt.uuid === this.model.processing);
    const product = this.passportProducts
      .find(product => product.uuid === this.model.product);

    if (this.cartService.determineMailaway(product, option)) {
      this.state.renewalConfirm = true;
    }
  }

  determineDateOfBirthMin() {
    const product = this.passportProducts.find(product => product.uuid === this.model.product);
    this.state.dateOfBirthMin = '1868-01-01';

    if (product.subtype === 'child-passport') {
      this.state.dateOfBirthMin = this.state.dobChild;
    }
  }

  finishWizard($event) {
    const success = $event.success || false;
    const switchTo = $event.switchTo || null;

    if (success) {
      this.state.renewalConfirm = false;
      this.state.renewalConfirmed = true;
      return;
    }

    if (switchTo) {
      const product = this.passportProducts
        .find(product => product.subtype === switchTo);

      // Set product
      this.model.product = product ? product.uuid : null;
    }

    // Empty options
    this.model.processing = null;
    this.state.renewalConfirm = false;

    this.updateProduct();
    this.updateCartOptions();
    this.updateOrderItem();
  }

  processingTotal() {
    const total = this.cartService.getOrderTotal();
    return this.$filter('currency')(total);
  }

  async addToCart() {
    if (this.processingForm.$invalid) {
      return;
    }

    const total = '';

    this.addTraveler();

    this.addItinerary();

    // Ecommerce Add To Cart
    this.ecommerceService.addToCart(this.cart);

    // Send event to Facebook Pixel
    if (typeof this.fbq === 'function') {
      this.fbq('track', 'AddToCart');
    }

    this.state.submitting = true;

    // Save cart and continue
    try {
      const cart = await this.cartService.saveCart(this.cart);

      if (cart) {
        window.location.href = this.laroute.route('checkout.account');
      }
    } catch (e) {
      this.state.submitting = false;
      throw e;
    }
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/components/passport-processing/passport-processing.component.ts